import { Link } from "react-router-dom";

import { useExpenses, useOverview } from "../api/hooks";
import type { NowJob, Overview, OverviewJob, OverviewMonth, OverviewStats } from "../api/types";
import { LoadError, Loading, useYear, Stale } from "../components/Layout";
import { dshort, hm, money, num } from "../lib/format";

export function OverviewPage() {
  const year = useYear();
  const q = useOverview(year);
  if (q.isPending) return <Loading />;
  if (q.error) return <LoadError error={q.error} />;
  return (
    <Stale on={q.isPlaceholderData}>
      <OverviewView data={q.data} />
    </Stale>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const KINDS: { key: keyof Omit<OverviewMonth, "month">; name: string }[] = [
  { key: "job", name: "On job" },
  { key: "onsite", name: "On site (no job yet)" },
  { key: "base", name: "Base day" },
  { key: "holiday", name: "Holiday" },
  { key: "doil", name: "DOIL used" },
  { key: "off", name: "Off" },
  { key: "quarantine", name: "Quarantine" },
];

const plural = (n: number, one: string, many = `${one}s`) => `${num(n)} ${n === 1 ? one : many}`;
const dates = (j: Pick<OverviewJob, "first" | "last">) =>
  j.first === j.last ? dshort(j.first) : `${dshort(j.first)} – ${dshort(j.last)}`;
/** Shorter, with the year: "13 Sep – 6 Nov 2019", or "30 Dec 2025 – 2 Jan 2026" across New Year. */
const dm = (iso: string) => dshort(iso).slice(4);
const span = (j: OverviewJob) => {
  const [a, b] = [j.first.slice(0, 4), j.last.slice(0, 4)];
  if (j.first === j.last) return `${dm(j.first)} ${a}`;
  return a === b ? `${dm(j.first)} – ${dm(j.last)} ${b}` : `${dm(j.first)} ${a} – ${dm(j.last)} ${b}`;
};

/** A job's name, linking to it; days only on the work calendar link to the planner, to create the job. */
function NowName({ j }: { j: NowJob }) {
  if (j.id !== null) return <Link to={`/job/${j.id}`}>{j.name}</Link>;
  return (
    <Link
      to={`/year/${j.first.slice(0, 4)}`}
      title="On your work calendar, with no job yet: create it on the planner"
    >
      {j.name}
    </Link>
  );
}

const nowKey = (j: NowJob) => (j.id !== null ? `job-${j.id}` : `cal-${j.first}-${j.name}`);

function Tile({ value, label, sub }: { value: string; label: string; sub?: React.ReactNode }) {
  return (
    <div className="tile">
      {/* --len lets long figures (£146,216.23) shrink to fit the tile */}
      <span className="tile-value" style={{ "--len": value.length } as React.CSSProperties}>
        {value}
      </span>
      <span className="tile-label">{label}</span>
      {sub ? <span className="tile-sub">{sub}</span> : null}
    </div>
  );
}

function OverviewView({ data }: { data: Overview }) {
  const { now, year: y, career } = data;
  const s = y.stats;
  const t = career.totals;
  const showHours = t.shift_days > 0;
  const expenses = useExpenses();
  const thisYear = now.today.slice(0, 4) === String(y.year);

  return (
    <>
      <div className="page-head">
        <h1>Overview</h1>
      </div>

      <section aria-labelledby="now-h" className="panel overview-now">
        <h2 id="now-h" className="visually-hidden">
          Right now
        </h2>
        <div>
          <span className="tile-label">Today</span>
          {now.current.length ? (
            now.current.map((j) => (
              <p key={nowKey(j)} className="now-job">
                <NowName j={j} /> <span className="muted small">{dates(j)}</span>
              </p>
            ))
          ) : (
            <p className="now-job muted">No job today</p>
          )}
        </div>
        <div>
          <span className="tile-label">Coming up</span>
          {now.upcoming.length ? (
            <ul className="now-list">
              {now.upcoming.map((j) => (
                <li key={nowKey(j)}>
                  <NowName j={j} />
                  <span className="muted small">
                    {dates(j)} · {plural(j.days, "day")}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="now-job muted">Nothing in the next two weeks</p>
          )}
        </div>
        <div className="now-figures">
          <Tile value={num(career.doil_balance_now)} label="DOILs today" />
          <Tile value={num(career.holiday_remaining_now)} label="Holiday days left" />
        </div>
      </section>

      <section aria-labelledby="year-h" className="overview-section">
        <div className="section-head">
          <h2 id="year-h">{y.year}</h2>
          <span className="small">
            <Link to={`/year/${y.year}`}>Planner</Link> · <Link to={`/year/${y.year}/jobs`}>Jobs</Link> ·{" "}
            <Link to={`/year/${y.year}/doils`}>DOILs</Link>
          </span>
        </div>
        <div className="tiles">
          <Tile
            value={num(s.worked_days)}
            label="Days worked"
            sub={s.booked_ahead ? `${num(s.booked_ahead)} still to come` : `${num(s.base_days)} base`}
          />
          <Tile value={num(s.jobs)} label="Jobs" sub={plural(s.job_days, "job day")} />
          {expenses && <Tile value={money(s.expenses)} label="Expenses" sub={`${num(s.mileage)} miles`} />}
          {showHours && (
            <Tile
              value={hm(s.hours_minutes)}
              label="Hours"
              sub={s.overtime_minutes ? `${hm(s.overtime_minutes)} overtime` : plural(s.shift_days, "shift")}
            />
          )}
          <Tile
            value={num(y.summary.doil_accrued)}
            label="DOILs earned"
            sub={`${num(y.summary.doil_used)} used · ${num(thisYear ? y.summary.doil_now : y.summary.doil_end)} ${thisYear ? "now" : "at year end"}`}
          />
          <Tile
            value={num(y.summary.holiday_taken)}
            label="Holidays taken"
            sub={`${num(y.summary.holiday_remaining)} left`}
          />
          <Tile value={num(s.weekends_worked)} label="Weekend days worked" />
          <Tile value={num(s.bank_holidays_worked)} label="Bank holidays worked" />
        </div>

        <div className="two-col" style={{ marginTop: "1.25rem" }}>
          <div className="panel">
            <h3>Month by month</h3>
            <MonthChart months={y.months} />
          </div>
          {(expenses || y.roles.some((r) => r.jobs)) && (
            <div className="panel">
              {expenses && (
                <>
                  <h3>Top jobs by expenses</h3>
                  <JobTable jobs={y.top_jobs} show="total" empty={`No jobs in ${y.year}.`} />
                </>
              )}
              {y.roles.some((r) => r.jobs) && (
                <>
                  <h3 style={expenses ? { marginTop: "1.25rem" } : undefined}>Roles</h3>
                  <RoleTable roles={y.roles.filter((r) => r.jobs)} />
                </>
              )}
            </div>
          )}
        </div>
      </section>

      <section aria-labelledby="career-h" className="overview-section">
        <div className="section-head">
          <h2 id="career-h">Career</h2>
          <span className="muted small">
            {career.first_year === career.last_year
              ? career.first_year
              : `${career.first_year} – ${career.last_year}`}
            {` · ${plural(t.years, "year")} with work recorded`}
          </span>
        </div>
        <div className="tiles">
          <Tile value={num(t.jobs)} label="Jobs" sub={plural(t.job_days, "job day")} />
          <Tile
            value={num(t.worked_days)}
            label="Days worked"
            sub={t.booked_ahead ? `incl. ${num(t.booked_ahead)} booked ahead` : undefined}
          />
          {expenses && <Tile value={money(t.expenses)} label="Expenses" sub={`${num(t.mileage)} miles`} />}
          {showHours && (
            <Tile
              value={hm(t.hours_minutes)}
              label="Hours"
              sub={t.overtime_minutes ? `${hm(t.overtime_minutes)} overtime` : plural(t.shift_days, "shift")}
            />
          )}
          <Tile
            value={num(t.doil_earned)}
            label="DOILs earned"
            sub={`${num(t.doil_used)} used · ${num(t.doil_sold)} sold`}
          />
          <Tile value={num(t.holiday_taken)} label="Holidays taken" />
          <Tile value={num(t.weekends_worked)} label="Weekend days worked" />
          <Tile value={num(t.bank_holidays_worked)} label="Bank holidays worked" />
        </div>

        <div className="panel" style={{ marginTop: "1.25rem" }}>
          <h3>Year by year</h3>
          <YearTable data={data} showHours={showHours} expenses={expenses} />
        </div>

        <div className="two-col" style={{ marginTop: "1.25rem" }}>
          <div className="panel">
            <h3>Longest jobs</h3>
            <JobTable jobs={career.longest_jobs} show="days" empty="No jobs yet." withYear />
          </div>
          {expenses && (
            <div className="panel">
              <h3>Biggest expenses</h3>
              <JobTable jobs={career.top_expense_jobs} show="total" empty="No jobs yet." withYear />
            </div>
          )}
          <div className="panel">
            <h3>Roles</h3>
            {career.roles.length ? <RoleTable roles={career.roles} /> : <p className="muted">No jobs yet.</p>}
          </div>
          {career.cities.length > 0 && (
            <div className="panel">
              <h3>Cities abroad</h3>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>City</th>
                      <th className="num">Jobs</th>
                      <th className="num">Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {career.cities.map((c) => (
                      <tr key={c.city}>
                        <td>{c.city}</td>
                        <td className="num">{c.jobs}</td>
                        <td className="num">{c.days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function MonthChart({ months }: { months: OverviewMonth[] }) {
  return (
    <>
      <div className="month-chart">
        {months.map((m) => {
          const parts = KINDS.filter((k) => m[k.key] > 0);
          const label = `${MONTHS[m.month - 1]}: ${parts.length ? parts.map((k) => `${k.name} ${m[k.key]}`).join(", ") : "nothing marked"}`;
          return (
            <div key={m.month} className="month-col" title={label} aria-label={label} role="img">
              <div className="month-bar">
                {parts.map((k) => (
                  <i key={k.key} className={`key-${k.key}`} style={{ height: `${(m[k.key] / 31) * 100}%` }} />
                ))}
              </div>
              <span className="month-name">{MONTHS[m.month - 1].slice(0, 1)}</span>
            </div>
          );
        })}
      </div>
      <ul className="legend legend-inline">
        {KINDS.filter((k) => k.key !== "quarantine" || months.some((m) => m.quarantine > 0)).map((k) => (
          <li key={k.key}>
            <i className={`key-${k.key}`} />
            {k.name}
          </li>
        ))}
      </ul>
    </>
  );
}

function JobTable({
  jobs,
  show,
  empty,
  withYear = false,
}: {
  jobs: OverviewJob[];
  show: "total" | "days";
  empty: string;
  withYear?: boolean;
}) {
  if (!jobs.length) return <p className="muted">{empty}</p>;
  return (
    <div className="table-wrap">
      <table className="job-list">
        <thead>
          <tr>
            <th>Job</th>
            <th>Dates</th>
            {show === "total" ? <th className="num">Expenses</th> : <th className="num">Days</th>}
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id}>
              <td>
                <Link to={`/job/${j.id}`}>{j.name}</Link>
              </td>
              <td className="muted">{withYear ? span(j) : dates(j)}</td>
              <td className="num">{show === "total" ? money(j.total) : j.days}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RoleTable({ roles }: { roles: Overview["career"]["roles"] }) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Role</th>
            <th className="num">Jobs</th>
            <th className="num">Days</th>
          </tr>
        </thead>
        <tbody>
          {roles.map((r) => (
            <tr key={r.code || "none"}>
              <td>{r.name}</td>
              <td className="num">{r.jobs}</td>
              <td className="num">{r.days}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function YearTable({ data, showHours, expenses }: { data: Overview; showHours: boolean; expenses: boolean }) {
  const { career } = data;
  const rows = [...career.years].reverse(); // newest first
  const most = Math.max(1, ...rows.map((r) => r.worked_days));
  const doil = (v: number | null) => (v === null ? <span className="muted">–</span> : num(v));
  const cells = (r: OverviewStats) => (
    <>
      <td className="num">{r.jobs}</td>
      <td className="num">{r.worked_days}</td>
      <td className="num">{num(r.holiday_taken)}</td>
      <td className="num">{doil(r.doil_earned)}</td>
      <td className="num">{doil(r.doil_used)}</td>
      {expenses && <td className="num opt">{num(r.mileage)}</td>}
      {expenses && <td className="num opt">{money(r.expenses)}</td>}
      {showHours && <td className="num opt">{r.shift_days ? hm(r.hours_minutes) : ""}</td>}
    </>
  );
  return (
    <div className="table-wrap">
      <table className="years">
        <thead>
          <tr>
            <th>Year</th>
            <th className="bar-cell opt">
              <span className="visually-hidden">Days worked, as a bar</span>
            </th>
            <th className="num">Jobs</th>
            <th className="num">Days worked</th>
            <th className="num">Holidays</th>
            <th className="num">DOILs earned</th>
            <th className="num">DOILs used</th>
            {expenses && <th className="num opt">Miles</th>}
            {expenses && <th className="num opt">Expenses</th>}
            {showHours && <th className="num opt">Hours</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.year} className={r.year === data.year.year ? "is-current" : undefined}>
              <td>
                <Link to={`/year/${r.year}/overview`}>{r.year}</Link>
                {r.legacy && (
                  <span className="muted small" title="DOILs from the history typed in on the DOILs page">
                    {" "}
                    history
                  </span>
                )}
              </td>
              <td className="bar-cell opt" aria-hidden="true">
                <span className="year-bar" style={{ width: `${(r.worked_days / most) * 100}%` }} />
              </td>
              {cells(r)}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            <td className="bar-cell opt" />
            {cells(career.totals)}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
